import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import LifeEventCheckbox from './LifeEventCheckbox';
import YesNoRadio from './YesNoRadio';
import CurrencyInput from './CurrencyInput';

const EMPTY_CHILD = { name: '', dob: '' };

/**
 * Section 2 — Family & Personal updates.
 *
 * Mirrors the cashflow style: a vertical list of life-event checkboxes
 * that expand into conditional fields when checked. At the bottom there
 * is a single "None of the above" option.
 */
export default function StepFamilyPersonal({ data, onChange }) {
  const value = data || {};

  function update(field, val) {
    const next = { ...value, [field]: val };
    if (field !== 'none_of_the_above' && val) {
      next.none_of_the_above = false;
    }
    onChange(next);
  }

  function setNone(checked) {
    if (checked) {
      onChange({ none_of_the_above: true, notes: value.notes });
    } else {
      onChange({ ...value, none_of_the_above: false });
    }
  }

  // Children list helpers
  const children = value.new_children && value.new_children.length > 0
    ? value.new_children
    : [{ ...EMPTY_CHILD }];

  function updateChild(idx, field, val) {
    const next = children.map((c, i) => (i === idx ? { ...c, [field]: val } : c));
    update('new_children', next);
  }

  function addChild() {
    update('new_children', [...children, { ...EMPTY_CHILD }]);
  }

  return (
    <div>
      <SectionHeader
        number={2}
        title="Situation Updates — Family &amp; Personal"
        tip="Check any that apply since we last met. You may be prompted
          for a few extra details. If nothing has changed, check the
          option at the bottom of the list."
        showAnnualNote={false}
      />

      <LifeEventCheckbox
        id="married"
        label="Married"
        checked={value.married}
        onChange={(c) => update('married', c)}
      >
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label">Partner name <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              value={value.married_partner_name || ''}
              onChange={(e) => update('married_partner_name', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Partner DOB <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              value={value.married_partner_dob || ''}
              onChange={(e) => update('married_partner_dob', e.target.value)}
            />
          </div>
        </div>
        <YesNoRadio
          id="married_beneficiaries"
          label="Have you or will you update your beneficiaries and/or estate planning documents?"
          value={value.married_updated_beneficiaries}
          onChange={(v) => update('married_updated_beneficiaries', v)}
        />
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="divorced"
        label="Divorced / separated"
        checked={value.divorced}
        onChange={(c) => update('divorced', c)}
      >
        <YesNoRadio
          id="divorced_beneficiaries"
          label="Have you or will you update your beneficiaries and/or estate planning documents?"
          value={value.divorced_updated_beneficiaries}
          onChange={(v) => update('divorced_updated_beneficiaries', v)}
        />
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="widowed"
        label="Widowed"
        checked={value.widowed}
        onChange={(c) => update('widowed', c)}
      >
        <YesNoRadio
          id="widowed_beneficiaries"
          label="Have you or will you update your beneficiaries and/or estate planning documents?"
          value={value.widowed_updated_beneficiaries}
          onChange={(v) => update('widowed_updated_beneficiaries', v)}
        />
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="new_child"
        label="Had a baby / adopted a child"
        checked={value.new_child}
        onChange={(c) => update('new_child', c)}
      >
        {children.map((c, idx) => (
          <div className="row g-2 mb-2" key={idx}>
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'Name *' : `Name (#${idx + 1})`}</label>
              <input
                type="text"
                className="form-control"
                value={c.name || ''}
                onChange={(e) => updateChild(idx, 'name', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{idx === 0 ? 'DOB *' : 'DOB'}</label>
              <input
                type="date"
                className="form-control"
                value={c.dob || ''}
                onChange={(e) => updateChild(idx, 'dob', e.target.value)}
              />
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-2"
          onClick={addChild}
        >
          <i className="bi bi-plus-lg me-1" />
          Add another child
        </button>
        <YesNoRadio
          id="new_child_beneficiaries"
          label="Have you or will you update your beneficiaries and/or estate planning documents?"
          value={value.new_child_updated_beneficiaries}
          onChange={(v) => update('new_child_updated_beneficiaries', v)}
        />
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="supporting_family"
        label="Began supporting or caring for a family member"
        checked={value.supporting_family}
        onChange={(c) => update('supporting_family', c)}
      >
        <div className="row g-2">
          <div className="col-md-4">
            <CurrencyInput
              label="Estimated annual expense"
              id="supporting_family_annual_expense"
              value={value.supporting_family_annual_expense}
              onChange={(v) => update('supporting_family_annual_expense', v)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Potential duration</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. 5 years"
              value={value.supporting_family_duration || ''}
              onChange={(e) => update('supporting_family_duration', e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Name &amp; relationship</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Mother — Jane Doe"
              value={value.supporting_family_relation || ''}
              onChange={(e) => update('supporting_family_relation', e.target.value)}
            />
          </div>
        </div>
      </LifeEventCheckbox>

      <LifeEventCheckbox
        id="moved"
        label="Moved / planning to relocate"
        checked={value.moved}
        onChange={(c) => update('moved', c)}
      >
        <label className="form-label">If moved, new address</label>
        <textarea
          className="form-control"
          rows={2}
          value={value.new_address || ''}
          onChange={(e) => update('new_address', e.target.value)}
        />
      </LifeEventCheckbox>

      <div className="form-check mt-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="family_none"
          checked={!!value.none_of_the_above}
          onChange={(e) => setNone(e.target.checked)}
        />
        <label className="form-check-label fw-semibold" htmlFor="family_none">
          None of the above has changed since we last met.
        </label>
      </div>

      <NotesInput
        value={value.notes}
        onChange={(v) => update('notes', v)}
      />
    </div>
  );
}
